import {
    Alert,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import Container from 'src/components/atoms/Container/Container';
import Heading from 'src/components/atoms/Heading/Heading';
import Select, { GroupedOption } from 'src/components/atoms/Select/Select';
import Searchbar from 'src/components/molecules/Searchbar/Searchbar';
import { Inheritance, ScopeEnum } from 'src/domain';
import { InheritanceSettingsRow } from 'src/domain/table/inheritance-settings.row';
import { useCluster, useScope } from 'src/hooks';
import { useCountry } from 'src/hooks/country/useCountry';
import { useInheritanceSettingsSimulationRows } from 'src/hooks/inheritance-settings/simulation/useInheritanceSettingsSimulationRows';
import { storeInheritanceFormatter } from 'src/utils/splitLevels';

enum Stores {
    All = 'All',
    Affected = 'Affected',
    NotAffected = 'Not affected',
}

interface InheritanceTableImplProps {
    moduleString: string;
    scope: ScopeEnum;
}

const InheritanceTableImpl: FC<InheritanceTableImplProps> = ({ moduleString, scope }) => {
    const cluster = useCluster();
    const country = useCountry();

    const { data } = useInheritanceSettingsSimulationRows();

    const [selected, setSelected] = useState<Stores>(Stores.Affected);
    const [sortAscending, setSortAscending] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const options = useMemo(
        (): GroupedOption[] => [
            {
                isHeader: false,
                value: Stores.Affected,
                display: 'Stores affected by changes',
            },
            {
                isHeader: false,
                value: Stores.NotAffected,
                display: 'Stores not affected by changes',
            },
            {
                isHeader: false,
                value: Stores.All,
                display: 'All',
            },
        ],
        []
    );

    const getStoreInheritance = useCallback(
        (store: InheritanceSettingsRow) => {
            if (moduleString === 'noosShare') {
                const noosInheritance = store.noosShare;
                if (noosInheritance.inheritance === Inheritance.Historical) {
                    return noosInheritance.noosActualInheritance;
                }
                return noosInheritance.inheritance;
            }
            return store[moduleString] as Inheritance;
        },
        [moduleString]
    );

    const sortStores = useCallback(
        (stores: InheritanceSettingsRow[]) => {
            return stores.sort((a, b) => {
                if (sortAscending) {
                    return a.storeName?.localeCompare(b.storeName || '') || 0;
                } else {
                    return b.storeName?.localeCompare(a.storeName || '') || 0;
                }
            });
        },

        [sortAscending]
    );

    const filterStoresByUserInput = useCallback(
        (stores: InheritanceSettingsRow[]) => {
            return stores.filter((store) => {
                const storeName = store.storeName?.toLowerCase();

                return storeName?.includes(searchQuery.toLowerCase());
            });
        },
        [searchQuery]
    );

    const filteredStores = useMemo(() => {
        switch (selected) {
            case Stores.Affected:
                return sortStores(
                    filterStoresByUserInput(
                        data.filter((store) => {
                            if (scope === ScopeEnum.PARTNER) {
                                return getStoreInheritance(store) === Inheritance.Partner;
                            }
                            if (scope === ScopeEnum.CLUSTER) {
                                return getStoreInheritance(store) === Inheritance.Cluster;
                            }
                        })
                    )
                );
            case Stores.NotAffected:
                return sortStores(
                    filterStoresByUserInput(
                        data.filter((store) => {
                            if (scope === ScopeEnum.PARTNER) {
                                return getStoreInheritance(store) !== Inheritance.Partner;
                            }
                            if (scope === ScopeEnum.CLUSTER) {
                                return getStoreInheritance(store) !== Inheritance.Cluster;
                            }
                        })
                    )
                );
            case Stores.All:
                return sortStores(filterStoresByUserInput(data));
        }
    }, [data, filterStoresByUserInput, selected, sortStores, scope, getStoreInheritance]);

    const title = useMemo(() => {
        switch (scope) {
            case ScopeEnum.PARTNER:
                return country;
            case ScopeEnum.CLUSTER:
                return cluster;
        }
    }, [cluster, country, scope]);

    const handleSelect = useCallback((value: string) => {
        if (value) setSelected(value as Stores);
    }, []);

    const handleUserInput = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchQuery(event.target.value);
    }, []);

    const handleResetInputField = useCallback(() => {
        setSearchQuery('');
    }, []);

    const handleSorting = useCallback(() => {
        setSortAscending((prev) => !prev);
    }, []);

    return (
        <StyledContainer>
            <Heading>{`Inheritance - ${title}`}</Heading>
            <Select
                fullWidth
                activeItem={selected}
                options={options}
                onSelect={handleSelect}
                label="Select stores to show"
                useMargin={false}
            />
            <Searchbar
                label="Search"
                placeholder="Search amongst stores"
                updateUserInput={handleUserInput}
                resetInputField={handleResetInputField}
                value={searchQuery}
            />
            {filteredStores.length === 0 && <Alert severity="info">No stores found</Alert>}
            {filteredStores.length > 0 && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel onClick={handleSorting} direction={!sortAscending ? 'asc' : 'desc'}>
                                        Store name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Inheriting from</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredStores.map((store) => {
                                const storeInheritance = getStoreInheritance(store);

                                const formattedInheritance = storeInheritanceFormatter(
                                    storeInheritance,
                                    store.clusterName,
                                    store.countryName,
                                    moduleString === 'noosShare'
                                );
                                return (
                                    <TableRow
                                        key={store.storeId}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {store.storeName}
                                        </TableCell>
                                        <TableCell>{formattedInheritance}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </StyledContainer>
    );
};

type InheritanceTableProps = Pick<InheritanceTableImplProps, 'moduleString'>;
export const InheritanceTable: FC<InheritanceTableProps> = ({ moduleString }) => {
    const scope = useScope();

    if (scope !== ScopeEnum.PARTNER) {
        return null;
    }

    return <InheritanceTableImpl moduleString={moduleString} scope={scope} />;
};

const StyledContainer = styled(Container)(({ theme }) => ({
    maxWidth: 600,
    maxHeight: 700,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
}));
